import {
  buildAnswerByResponseIdQueryKey,
  useAnswerByResponseId,
} from "@/hooks/answer-by-response-id.hook";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useResponseById } from "@/hooks/response-by-id.hook";
import { findAndReplaceById } from "@/lib/quizzes";
import { GetResponseByIdResponse } from "@/requests/questionnaries/response-by-id.request";
import { Choice, CompositeChoice } from "@/types/questionnaries";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

type QuestionContext = {
  responseId: string;
  question: GetResponseByIdResponse | undefined;
  answer: CompositeChoice | undefined;
  quizId: string;
  setQuizId: (quizId: string) => void;
  choices: Choice[];
  setChoices: (choices: Choice[]) => void;
  updateChoice: (choice: Choice) => void;
  isSuggestionApplied: boolean;
  setIsSuggestionApplied: (value: boolean) => void;
  isSaving: boolean;
  setIsSaving: (value: boolean) => void;
  lastSavedTime: Date | null;
  setLastSavedTime: (value: Date) => void;
  isValidSelection: boolean;
  setIsValidSelection: (value: boolean) => void;
  isAutosaveEnabled: boolean;
  setIsAutosaveEnabled: (value: boolean) => void;
  isDirty: boolean;
  setIsDirty: (value: boolean) => void;
};

const QuestionContext = createContext<QuestionContext | undefined>(undefined);

type QuestionProviderProps = {
  children: ReactNode;
  responseId: string;
  quizId: string;
};

export const QuestionProvider: FC<QuestionProviderProps> = (props) => {
  const queryClient = useQueryClient();

  const { data: userProfile } = useCurrentUserProfile();

  const [quizId, setQuizId] = useState<string>(props.quizId);

  const [choices, setChoices] = useState<Choice[]>([]);

  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);

  const [isValidSelection, setIsValidSelection] = useState(false);

  const [isAutosaveEnabled, setIsAutosaveEnabled] = useState(true);

  const [isDirty, setIsDirty] = useState(false);

  const updateChoice = (newChoice: Choice) => {
    setChoices(findAndReplaceById(choices, newChoice.id, newChoice));
  };

  const { data: question } = useResponseById({
    companyId: userProfile?.companyId || "",
    quizId: props.quizId,
    responseId: props.responseId,
  });

  const { data: answer, dataUpdatedAt: answerUpdatedAt } =
    useAnswerByResponseId({
      companyId: userProfile?.companyId || "",
      quizId: props.quizId,
      responseId: props.responseId,
    });

  const [isSuggestionApplied, setIsSuggestionApplied] =
    useState<boolean>(false);

  useEffect(() => {
    if (answer) {
      setChoices(answer.choices);
    }
  }, [answerUpdatedAt]);

  /**
   * When the Context is invalidated, we need to invalidate the answer query
   * to ensure that the answer is updated when the user comes back.
   */
  useEffect(() => {
    // Wait a tick to prevent the effect from running on mount
    // All of this because the AnimatePresence component is unmounting and mounting
    let didMount = false;

    const timer = setTimeout(() => {
      didMount = true;
    }, 0);

    return () => {
      clearTimeout(timer);
      if (didMount) {
        const answerQueryKey = buildAnswerByResponseIdQueryKey({
          companyId: userProfile?.companyId || "",
          quizId: props.quizId,
          responseId: props.responseId,
        });

        queryClient.invalidateQueries({
          queryKey: answerQueryKey,
        });
      }
    };
  }, []);

  return (
    <QuestionContext.Provider
      value={{
        responseId: props.responseId,
        question,
        answer,
        quizId,
        setQuizId,
        choices,
        setChoices,
        updateChoice,
        isSuggestionApplied,
        setIsSuggestionApplied,
        isSaving,
        setIsSaving,
        lastSavedTime,
        setLastSavedTime,
        isValidSelection,
        setIsValidSelection,
        isAutosaveEnabled,
        setIsAutosaveEnabled,
        isDirty,
        setIsDirty,
      }}
    >
      {props.children}
    </QuestionContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useQuestion = (): QuestionContext => {
  const context = useContext(QuestionContext);
  if (!context) {
    throw new Error("useQuestion must be used within a QuestionProvider");
  }
  return context;
};
